import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Star } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/czxzhc8c_Stamp.JPG" 
              alt="Energy Performance Direct" 
              className="h-16 mb-4 brightness-110"
            />
            <p className="text-sm mb-4">
              Professional energy assessments and certifications across Exeter, South West UK, and nationwide.
            </p>
            <div className="flex gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/ejoqsqlu_image.png" 
                alt="Elmhurst Energy" 
                className="h-10 opacity-80 hover:opacity-100"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/gfybwp7x_image.png" 
                alt="TrustMark" 
                className="h-10 opacity-80 hover:opacity-100"
              />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-green-400" data-testid="footer-link-home">Home</Link></li>
              <li><Link to="/services" className="hover:text-green-400" data-testid="footer-link-services">Services</Link></li>
              <li><Link to="/case-studies" className="text-green-400 font-semibold hover:text-green-300 inline-flex items-center gap-1" data-testid="footer-link-case-studies">Case Studies →</Link></li>
              <li><Link to="/about" className="hover:text-green-400" data-testid="footer-link-about">About Us</Link></li>
              <li><Link to="/accreditations" className="hover:text-green-400" data-testid="footer-link-accreditations">Accreditations</Link></li>
              <li><Link to="/reviews" className="hover:text-green-400" data-testid="footer-link-reviews">Reviews</Link></li>
              <li><Link to="/faq" className="hover:text-green-400" data-testid="footer-link-faq">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-green-400" data-testid="footer-link-contact">Contact</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold mb-4">Services</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/commercial-epc" className="hover:text-green-400" data-testid="footer-link-commercial-epc">Commercial EPC (NDEPC)</Link></li>
              <li><Link to="/services/commercial_advice" className="hover:text-green-400">MEES Commercial Advice</Link></li>
              <li><Link to="/sap-calculations" className="hover:text-green-400">SAP Calculations</Link></li>
              <li><Link to="/lease-plans" className="hover:text-green-400">Lease Plans</Link></li>
              <li><Link to="/domestic-epc-exeter" className="hover:text-green-400">Domestic EPC</Link></li>
              <li><Link to="/floor-plans-exeter" className="hover:text-green-400">Floor Plans</Link></li>
              <li><Link to="/u-value-calculations" className="hover:text-green-400">U-Value Calculations</Link></li>
              <li><Link to="/services/retrofit" className="hover:text-green-400">Retrofit Coordinator</Link></li>
              <li><Link to="/independent-heat-load-calculations" className="hover:text-green-400">Heat Load Calculations</Link></li>
              <li><Link to="/methodology" className="hover:text-green-400 font-semibold">Our Methodology &rarr;</Link></li>
              <li><Link to="/author/zak-henning" className="hover:text-green-400">About Zak Henning</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 text-green-400 flex-shrink-0" />
                <span>Exeter, Devon, UK</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-green-400 flex-shrink-0" />
                <a href="tel:01392980077" className="hover:text-green-400">01392 980077</a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-green-400 flex-shrink-0" />
                <a href="tel:07977215934" className="hover:text-green-400">07977 215934</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-400 flex-shrink-0" />
                <a href="mailto:info@energyperformancedirect.co.uk" className="hover:text-green-400 break-all">
                  info@energyperformancedirect.co.uk
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Review Trust Section */}
        <div className="mt-8 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <Link to="/reviews" className="text-center hover:opacity-80 transition-opacity">
              <div className="flex items-center gap-1 mb-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              </div>
              <p className="text-xs text-gray-400">Google Reviews · 4.9/5</p>
            </Link>
            <Link to="/reviews" className="text-center hover:opacity-80 transition-opacity">
              <div className="flex items-center gap-1 mb-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              </div>
              <p className="text-xs text-gray-400">FreeIndex · 5.0/5</p>
            </Link>
            <Link to="/reviews" className="text-center hover:opacity-80 transition-opacity">
              <div className="text-green-400 font-semibold mb-1">✓ Verified</div>
              <p className="text-xs text-gray-400">Buy With Confidence</p>
            </Link>
          </div>
        </div>

        {/* Commercial EPC by Region - SEO Hub */}
        <div className="mt-8 pt-8 border-t border-gray-800">
          <h3 className="text-white font-semibold mb-3 text-sm">Commercial EPC by Region (NDEPC, NDEA, Level 3)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <p className="text-gray-300 font-semibold mb-1">Devon</p>
              <ul className="space-y-1">
                <li><Link to="/commercial-epc-devon" className="text-gray-400 hover:text-green-400">All Devon</Link></li>
                <li><Link to="/commercial-epc-exeter" className="text-gray-400 hover:text-green-400">Exeter</Link></li>
                <li><Link to="/commercial-epc-plymouth" className="text-gray-400 hover:text-green-400">Plymouth</Link></li>
                <li><Link to="/commercial-epc-torquay" className="text-gray-400 hover:text-green-400">Torquay</Link></li>
                <li><Link to="/commercial-epc-newton-abbot" className="text-gray-400 hover:text-green-400">Newton Abbot</Link></li>
                <li><Link to="/commercial-epc-barnstaple" className="text-gray-400 hover:text-green-400">Barnstaple</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-gray-300 font-semibold mb-1">Somerset</p>
              <ul className="space-y-1">
                <li><Link to="/commercial-epc-somerset" className="text-gray-400 hover:text-green-400">All Somerset</Link></li>
                <li><Link to="/commercial-epc-taunton" className="text-gray-400 hover:text-green-400">Taunton</Link></li>
                <li><Link to="/commercial-epc-bridgwater" className="text-gray-400 hover:text-green-400">Bridgwater</Link></li>
                <li><Link to="/commercial-epc-yeovil" className="text-gray-400 hover:text-green-400">Yeovil</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-gray-300 font-semibold mb-1">Cornwall</p>
              <ul className="space-y-1">
                <li><Link to="/commercial-epc-cornwall" className="text-gray-400 hover:text-green-400">All Cornwall</Link></li>
                <li><Link to="/commercial-epc-truro" className="text-gray-400 hover:text-green-400">Truro</Link></li>
                <li><Link to="/commercial-epc-falmouth" className="text-gray-400 hover:text-green-400">Falmouth</Link></li>
                <li><Link to="/commercial-epc-newquay" className="text-gray-400 hover:text-green-400">Newquay</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-gray-300 font-semibold mb-1">Dorset</p>
              <ul className="space-y-1">
                <li><Link to="/commercial-epc-dorset" className="text-gray-400 hover:text-green-400">All Dorset</Link></li>
                <li><Link to="/commercial-epc-bournemouth" className="text-gray-400 hover:text-green-400">Bournemouth</Link></li>
                <li><Link to="/commercial-epc-poole" className="text-gray-400 hover:text-green-400">Poole</Link></li>
                <li><Link to="/commercial-epc-dorchester" className="text-gray-400 hover:text-green-400">Dorchester</Link></li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Plus <Link to="/commercial-epc" className="text-green-400 hover:underline">main Commercial EPC pillar page</Link> · SBEM calculations and MEES advice available across all regions.
          </p>
        </div>

        {/* Service Areas - SEO Text */}
        <div className="mt-8 pt-8 border-t border-gray-800">
          <h3 className="text-white font-semibold mb-3 text-sm">Commercial & Domestic EPC Service Areas</h3>
          <p className="text-xs text-gray-400 leading-relaxed">
            <strong className="text-gray-300">Devon:</strong> Exeter, Plymouth, Torquay, Paignton, Barnstaple, Bideford, Tiverton, Crediton, Dartmouth, Totnes, Ivybridge, Honiton, Okehampton, Sidmouth, Newton Abbot, Tavistock, Exmouth, Dawlish. <strong className="text-gray-300">Cornwall:</strong> Truro, Bodmin, Newquay, Falmouth, St Austell, Penzance, Redruth, Camborne, Launceston, Bude. <strong className="text-gray-300">Somerset:</strong> Taunton, Bridgwater, Yeovil, Bath, Weston-super-Mare, Glastonbury, Frome, Wellington. <strong className="text-gray-300">Dorset:</strong> Bournemouth, Poole, Dorchester, Weymouth, Blandford Forum.
          </p>
        </div>

        {/* Registered business details — UK Companies Act compliance */}
        <div className="mt-8 pt-8 border-t border-gray-800">
          <h3 className="text-white font-semibold mb-3 text-sm">Registered Business Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-400">
            <div>
              <p className="text-gray-300 font-semibold mb-1">Energy Performance Direct Ltd</p>
              <p>Company number: 08296332</p>
              <p>Registered in England &amp; Wales</p>
              <p>Managing Director: Zak Henning</p>
              <p>Trading since 2009 &middot; Based in Exeter, Devon</p>
            </div>
            <div>
              <p className="text-gray-300 font-semibold mb-1">Contact &amp; VAT</p>
              <p>Telephone: 01392 980077</p>
              <p>Email: info@energyperformancedirect.co.uk</p>
              <p>Not VAT registered &mdash; no VAT is added to prices</p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
          <p>&copy; {new Date().getFullYear()} Energy Performance Direct. All rights reserved.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/privacy" className="hover:text-green-400" data-testid="footer-link-privacy">Privacy Policy (GDPR)</Link>
            <Link to="/terms" className="hover:text-green-400" data-testid="footer-link-terms">Terms & Conditions</Link>
            <Link to="/cookies" className="hover:text-green-400" data-testid="footer-link-cookies">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;